import {
  TemplateState,
  FeaturedAction,
  Platform,
  FeaturedActionSlotKey,
} from '../../../../store/types/template';
import { supportsGoogleContactlessMode } from '../../../../utils/googleNfc';
import {
  STAMP_WORDS,
  getStampImageKey,
  getStampShortValueKey,
  getStampLongValueKey,
} from './stampCardConstants';
import {
  createEmptyFeaturedActions,
  featuredActionsEqual,
  isFeaturedActionSlotFilled,
} from './featuredActionTypes';

/** Convert a camelCase string to snake_case. */
const camelToSnakeCase = (str: string): string =>
  str.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/** Convert all field IDs in a template fields object from camelCase to snake_case. */
export const convertFieldIdsToSnakeCase = (
  fields: Record<string, Record<string, any>>
): Record<string, Record<string, any>> => {
  const result: Record<string, Record<string, any>> = {};
  for (const [section, slots] of Object.entries(fields)) {
    result[section] = {};
    for (const [slot, field] of Object.entries(slots || {})) {
      if (field && field.id) {
        result[section][slot] = { ...field, id: camelToSnakeCase(field.id) };
      } else {
        result[section][slot] = field;
      }
    }
  }
  return result;
};

/** Convert a field label to a snake_case ID suitable for the API. */
export const labelToFieldId = (
  label: string,
  section: string,
  slot: string
): string => {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || `${section}_${slot}`;
};

export const formatTemplatePayload = (
  state: TemplateState,
  mode: 'create' | 'update' = 'create',
  localisationEnabled?: boolean | null,
  autoGenerateFieldId?: boolean
) => {
  const {
    templateName,
    templateType,
    description,
    centralTitle,
    defaultLanguage,
    images,
    _originalWatermarks,
    colors,
    fields,
    nfc,
    barcode,
    appLinking,
    notificationHeader,
    locations,
    beacons,
    loyaltyBalance,
    transitType,
    originalDefaultValues,
    dynamicForm,
    stampCount,
    _overrideNfc,
    _overrideBarcode,
    _overrideFeaturedActions,
    stampShortKey,
    stampLongKey,
    stampFeedbackTypes,
    notificationMessages,
    stampShortValues,
    stampLongValues,
  } = state;

  const clean = (
    obj: any,
    isFieldsObject = false,
    isImagesObject = false,
    isFieldObject = false
  ): any => {
    if (Array.isArray(obj)) {
      const cleanedArray = obj
        .map((v) => clean(v, isFieldsObject, isImagesObject, isFieldObject))
        .filter((v) => v !== undefined && v !== null && v !== '');
      if (mode === 'update') return cleanedArray;
      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    if (obj && typeof obj === 'object') {
      const cleaned: Record<string, any> = {};

      const isCurrentFieldObject =
        isFieldObject ||
        (isFieldsObject &&
          !Array.isArray(obj) &&
          ('id' in obj || 'label' in obj || 'defaultValue' in obj));

      Object.entries(obj).forEach(([k, v]) => {
        if (k.startsWith('_')) return;
        if (mode === 'create' && k === 'overrideFieldValue') return;
        const isNestedFieldsObject = isFieldsObject || k === 'fields';
        const isNestedImagesObject = isImagesObject || k === 'images';
        const isValueFieldObject =
          isNestedFieldsObject &&
          typeof v === 'object' &&
          v !== null &&
          !Array.isArray(v) &&
          ('id' in v || 'label' in v || 'defaultValue' in v);

        const cleanedValue = clean(
          v,
          isNestedFieldsObject,
          isNestedImagesObject,
          isValueFieldObject
        );

        if (
          isCurrentFieldObject &&
          mode === 'update' &&
          (k === 'label' || k === 'defaultValue') &&
          typeof v === 'string' &&
          v.trim() === ''
        ) {
          cleaned[k] = null;
        } else if (
          (isNestedFieldsObject || isNestedImagesObject) &&
          mode === 'update' &&
          cleanedValue === null
        ) {
          cleaned[k] = null;
        } else if (
          k === 'localisation' &&
          typeof cleanedValue === 'object' &&
          cleanedValue !== null &&
          !Array.isArray(cleanedValue)
        ) {
          if (Object.keys(cleanedValue).length > 0) {
            cleaned[k] = cleanedValue;
          } else if (mode === 'update') {
            cleaned[k] = null;
          }
        } else if (
          cleanedValue !== undefined &&
          cleanedValue !== null &&
          cleanedValue !== '' &&
          (Array.isArray(cleanedValue) ||
            typeof cleanedValue !== 'object' ||
            Object.keys(cleanedValue).length > 0)
        ) {
          cleaned[k] = cleanedValue;
        }
      });

      return cleaned;
    }

    if (typeof obj === 'string') {
      const trimmed = obj.trim();
      if (isFieldObject && mode === 'update' && trimmed === '') {
        return null;
      }
      return trimmed === '' ? undefined : trimmed;
    }

    return obj;
  };

  const allSections =
    mode === 'update' && originalDefaultValues
      ? new Set([...Object.keys(fields), ...Object.keys(originalDefaultValues)])
      : new Set(Object.keys(fields));

  // Collect all auto-generated IDs to detect duplicates
  const usedIds = new Set<string>();

  const filteredFields = Object.fromEntries(
    Array.from(allSections)
      .map((section) => {
        const sectionFields = fields[section as keyof typeof fields] || {};
        const sectionEntries = Object.entries(sectionFields)
          .map(([slot, field]) => {
            if (mode === 'update' && field === null) {
              return [slot, null];
            }
            if (autoGenerateFieldId && field) {
              const hasExistingId = field.id && field.id.trim() !== '';
              const hasContent =
                (field.label && field.label.trim() !== '') ||
                (field.defaultValue !== '' &&
                  field.defaultValue !== null &&
                  field.defaultValue !== undefined);
              if (hasExistingId) {
                usedIds.add(field.id.trim());
                return [slot, field];
              }
              if (hasContent) {
                let generatedId = labelToFieldId(
                  field.label || '',
                  section,
                  slot
                );
                // Deduplicate if this ID is already used
                if (usedIds.has(generatedId)) {
                  let suffix = 2;
                  while (usedIds.has(`${generatedId}_${suffix}`)) {
                    suffix++;
                  }
                  generatedId = `${generatedId}_${suffix}`;
                }
                usedIds.add(generatedId);
                return [slot, { ...field, id: generatedId }];
              }
              return null;
            }
            if (field && field.id && field.id.trim() !== '') {
              return [slot, field];
            }
            return null;
          })
          .filter((entry): entry is [string, any] => entry !== null);

        if (mode === 'update' && originalDefaultValues) {
          const originalSectionFields =
            originalDefaultValues[
              section as keyof typeof originalDefaultValues
            ];
          if (originalSectionFields) {
            Object.entries(originalSectionFields).forEach(
              ([slot, originalField]) => {
                if (originalField && !sectionFields[slot]) {
                  sectionEntries.push([slot, null]);
                }
              }
            );
          }
        }

        if (sectionEntries.length > 0) {
          return [section, Object.fromEntries(sectionEntries)];
        }
        return null;
      })
      .filter((entry): entry is [string, Record<string, any>] => entry !== null)
  );

  // Strip <a> wrapper from back fields in text mode (user toggled Link → Text but URL was preserved)
  const anchorRegex = /^<a\s+href=[''""'"](.*?)[''""'"]\s*>(.*?)<\/a>$/;
  if (filteredFields.back) {
    Object.entries(filteredFields.back).forEach(([slot, field]) => {
      if (!field) return;
      const isLink =
        field._isLink ?? anchorRegex.test(String(field.defaultValue ?? ''));
      const dv = field.defaultValue;
      if (!isLink || typeof dv !== 'string') return;
      const emptyAnchor = dv.match(
        /^<a\s+href=[''""'"](.*?)[''""'"]\s*><\/a>$/
      );
      if (emptyAnchor) {
        filteredFields.back[slot] = {
          ...field,
          defaultValue: `<a href="${emptyAnchor[1]}">${emptyAnchor[1]}</a>`,
        };
      } else if (
        !anchorRegex.test(dv) &&
        /^(https?:\/\/|mailto:|tel:)/i.test(dv)
      ) {
        filteredFields.back[slot] = {
          ...field,
          defaultValue: `<a href="${dv}">${dv}</a>`,
        };
      }
    });
  }

  const colorsPayload = {
    background: colors.passBackgroundColor,
    label: colors.titleFontColor,
    text: colors.bodyFontColor,
  };

  // Shared watermark
  const sharedWatermark = {
    color: images.shared.watermarkColor,
    position: images.shared.watermarkPosition,
  };

  const watermarkChanged = (platform: Platform) => {
    const original = _originalWatermarks?.[platform];
    const color = (value?: string) => value || 'light';
    const position = (value?: string) => value || 'bottomRight';
    return (
      !!original &&
      (color(images[platform].watermarkColor) !== color(original.color) ||
        position(images[platform].watermarkPosition) !==
          position(original.position))
    );
  };

  const handleImageField = (
    value: string | null | undefined,
    isUpdate: boolean
  ): string | null | undefined => {
    if (isUpdate) {
      if (value === null) return null;
      // Skip unchanged images (still the original URL from the API)
      if (value && value.startsWith('https://')) return undefined;
      if (value && value.trim() !== '') return value;
      return undefined;
    } else {
      return value && value.trim() !== '' ? value : undefined;
    }
  };

  // --- Apple ---
  const appleImages: Record<string, any> = {};
  const appleLogo = handleImageField(images.apple.logo, mode === 'update');
  const appleIcon = handleImageField(images.apple.icon, mode === 'update');
  const appleBanner = handleImageField(images.apple.banner, mode === 'update');
  const appleThumbnail = handleImageField(
    images.apple.thumbnail,
    mode === 'update'
  );

  if (appleLogo !== undefined) appleImages.logo = appleLogo;
  if (appleIcon !== undefined) appleImages.icon = appleIcon;
  if (appleBanner !== undefined) appleImages.banner = appleBanner;
  if (appleThumbnail !== undefined) appleImages.thumbnail = appleThumbnail;

  const appleWatermark = {
    color: images.apple.watermarkColor,
    position: images.apple.watermarkPosition,
  };
  if (appleBanner || (images.apple.banner && watermarkChanged('apple'))) {
    appleImages.watermark = appleWatermark;
  }

  // --- Google ---
  const googleImages: Record<string, any> = {};
  const googleLogo = handleImageField(images.google.logo, mode === 'update');
  const googleWideLogo = handleImageField(
    images.google.wideLogo,
    mode === 'update'
  );
  const googleBanner = handleImageField(
    images.google.banner,
    mode === 'update'
  );

  if (googleLogo !== undefined) googleImages.logo = googleLogo;
  if (googleWideLogo !== undefined) googleImages.wideLogo = googleWideLogo;
  if (googleBanner !== undefined) googleImages.banner = googleBanner;

  const googleWatermark = {
    color: images.google.watermarkColor,
    position: images.google.watermarkPosition,
  };

  if (googleBanner || (images.google.banner && watermarkChanged('google'))) {
    googleImages.watermark = googleWatermark;
  }

  // Shared images remain available as fallbacks when platform overrides exist.
  const imagesPayload: Record<string, any> = {};

  const bannerSource =
    templateType === 'stampCard' &&
    stampFeedbackTypes?.includes('changePassBanner')
      ? images.shared.stampZeroImage
      : images.shared.banner;
  const sharedBanner = handleImageField(bannerSource, mode === 'update');
  const sharedLogo = handleImageField(images.shared.logo, mode === 'update');
  const sharedIcon = handleImageField(images.shared.icon, mode === 'update');

  if (sharedBanner !== undefined) {
    imagesPayload.banner = sharedBanner;
  }
  if (sharedLogo !== undefined) {
    imagesPayload.logo = sharedLogo;
  }
  if (sharedIcon !== undefined) {
    imagesPayload.icon = sharedIcon;
  }

  // Handle shared watermark
  if (sharedBanner || (bannerSource && watermarkChanged('shared'))) {
    imagesPayload.watermark = sharedWatermark;
  }

  // Add per-system overrides if they exist or if in update mode with null values
  if (Object.keys(appleImages).length > 0) imagesPayload.apple = appleImages;
  if (Object.keys(googleImages).length > 0) imagesPayload.google = googleImages;

  const hasGoogleLinkTarget =
    !!appLinking.googleWebUrl ||
    !!appLinking.googleAppPackageName ||
    !!appLinking.googleAppPlayStoreUrl;

  const appLinkingPayload = {
    apple: {
      appStoreUrl: appLinking.appleAppStoreUrl.filter((u) => u && u.trim()),
    },
    google: clean({
      displayText:
        hasGoogleLinkTarget && appLinking.googleDisplayText
          ? { text: appLinking.googleDisplayText }
          : undefined,
      web: appLinking.googleWebUrl
        ? { url: appLinking.googleWebUrl }
        : undefined,
      app:
        appLinking.googleAppPackageName || appLinking.googleAppPlayStoreUrl
          ? {
              package_name: appLinking.googleAppPackageName,
              play_store_url: appLinking.googleAppPlayStoreUrl,
            }
          : undefined,
    }),
  };

  const loyaltyBalancePayload =
    mode === 'update'
      ? {
          enabled: loyaltyBalance.enabled,
          number_type: loyaltyBalance.numberType,
        }
      : loyaltyBalance.enabled
        ? {
            enabled: loyaltyBalance.enabled,
            number_type: loyaltyBalance.numberType,
          }
        : undefined;

  const buildNfcPayload = (nfc: any) => {
    const copy = { ...nfc };
    delete copy.googleNfc;
    return copy;
  };

  const nfcBase = nfc.enabled
    ? buildNfcPayload(nfc)
    : mode === 'update'
      ? { enabled: false, source: 'uuid' }
      : undefined;
  const nfcPayload =
    nfcBase && mode === 'update'
      ? { ...nfcBase, overrideNfc: _overrideNfc }
      : nfcBase;

  const barcodeBase =
    mode === 'update'
      ? barcode.enabled
        ? barcode
        : { enabled: false, type: 'qr', source: 'uuid', displayText: false }
      : barcode.enabled
        ? barcode
        : undefined;
  const barcodePayload =
    barcodeBase && mode === 'update'
      ? { ...barcodeBase, overrideBarcode: _overrideBarcode }
      : barcodeBase;

  const locationsPayload =
    mode === 'update'
      ? locations.length > 0
        ? locations.map((v) => clean(v))
        : []
      : locations.length > 0
        ? locations.map((v) => clean(v))
        : undefined;

  const beaconsPayload =
    mode === 'update'
      ? beacons.length > 0
        ? beacons.map((v) => clean(v))
        : []
      : beacons.length > 0
        ? beacons.map((v) => clean(v))
        : undefined;

  const dynamicFormPayload =
    dynamicForm && (dynamicForm.enabled === true || mode === 'update')
      ? dynamicForm.enabled === true
        ? clean({
            ...dynamicForm,
            buttonCtaText:
              dynamicForm.buttonText ??
              (dynamicForm as unknown as Record<string, unknown>).buttonCtaText,
          } as unknown as Record<string, unknown>)
        : { enabled: false }
      : undefined;

  let stampCardPayload: Record<string, unknown> | undefined;
  if (templateType === 'stampCard') {
    const shared = images.shared;
    const useBannerForStampImages =
      !stampFeedbackTypes?.includes('changePassBanner');
    const bannerImage =
      shared.banner != null && shared.banner !== '' ? shared.banner : null;
    const numericStampCount = Math.min(
      Math.max(Number(stampCount) ?? 2, 2),
      12
    );
    const stamps: Record<string, unknown> = {};
    for (let i = 0; i <= numericStampCount; i++) {
      const word = STAMP_WORDS[i];
      const shortKey = getStampShortValueKey(
        word
      ) as keyof typeof stampShortValues;
      const longKey = getStampLongValueKey(
        word
      ) as keyof typeof stampLongValues;
      const shortVal = stampShortValues?.[shortKey];
      const longVal = stampLongValues?.[longKey];
      const imageKey = getStampImageKey(word) as keyof typeof shared;
      const imageVal = shared[imageKey];
      const hasImage = imageVal != null && imageVal !== '';
      const message = notificationMessages?.[i]?.label ?? '';
      const hasShort = shortVal != null && shortVal !== '';
      const hasLong = longVal != null && longVal !== '';
      const hasWatermarkColor =
        shared.watermarkColor != null && shared.watermarkColor !== '';
      const hasWatermarkPosition =
        shared.watermarkPosition != null && shared.watermarkPosition !== '';
      const stampImage = useBannerForStampImages
        ? bannerImage
        : hasImage
          ? imageVal
          : null;
      stamps[word] = {
        message,
        shortValue: hasShort ? shortVal : null,
        longValue: hasLong ? longVal : null,
        image: stampImage,
        watermark: {
          color: hasWatermarkColor ? shared.watermarkColor : null,
          position: hasWatermarkPosition ? shared.watermarkPosition : null,
        },
      };
    }
    stampCardPayload = {
      stampCount: stampCount != null ? Number(stampCount) : 2,
      stampShortKey: stampShortKey ?? '',
      stampLongKey: stampLongKey ?? '',
      updateBannerOnStamp:
        stampFeedbackTypes?.includes('changePassBanner') ?? false,
      stamps,
    };
  }

  const payload = clean({
    name: templateName?.trim(),
    templateType: templateType,
    description,
    ...(localisationEnabled !== false && { defaultLanguage: defaultLanguage }),
    centralTitle: centralTitle,
    notificationHeader: notificationHeader,
    colors: colorsPayload,
    images: imagesPayload,
    fields: filteredFields,
    locations: locationsPayload,
    beacons: beaconsPayload,
    appLinking: appLinkingPayload,
    nfc: nfcPayload,
    barcode: barcodePayload,
    loyaltyBalance: loyaltyBalancePayload,
    ...(dynamicFormPayload !== undefined && {
      dynamicForm: dynamicFormPayload,
    }),
  });

  if (templateType === 'stampCard' && stampCardPayload) {
    payload.stampCard = stampCardPayload;
  }

  // google_nfc_enabled (Smart Tap) is only user-controllable on loyalty
  // templates; never send it for other types so unrelated edits can't toggle it.
  if (
    supportsGoogleContactlessMode(templateType) &&
    (mode === 'update' || nfc.enabled)
  ) {
    payload.googleNfcEnabled = nfc.googleNfc;
  }

  if (templateType === 'transit') {
    payload.transitType = transitType;
  }

  payload.chosenGoogleLogo = state.chosenGoogleLogo;

  const appleEnhancedDesignPayload = buildAppleEnhancedDesignPayload(
    state.appleEnhancedDesign,
    state._appleEnhancedDesignOnLoad,
    mode
  );
  if (appleEnhancedDesignPayload !== undefined) {
    payload.appleEnhancedDesign =
      mode === 'update' && _overrideFeaturedActions
        ? { ...appleEnhancedDesignPayload, overrideFeaturedActions: true }
        : appleEnhancedDesignPayload;
  }

  return payload;
};

const APPLE_ENHANCED_DESIGN_IMAGE_KEYS = [
  'artwork',
  'secondaryLogo',
  'venueMap',
] as const;

type AppleEnhancedDesignImageKey =
  (typeof APPLE_ENHANCED_DESIGN_IMAGE_KEYS)[number];

type FeaturedActionSlotPayload = Pick<FeaturedAction, 'type' | 'url'> | null;

type FeaturedActionsPayload = Record<
  FeaturedActionSlotKey,
  FeaturedActionSlotPayload
>;

type AppleEnhancedDesignPayload = {
  type: TemplateState['appleEnhancedDesign']['type'];
  images?: Record<string, string | null>;
  featuredActions?: FeaturedActionsPayload;
  overrideFeaturedActions?: true;
};

export const buildFeaturedActionSlotPayload = (
  slot: FeaturedAction
): FeaturedActionSlotPayload =>
  isFeaturedActionSlotFilled(slot) ? { type: slot.type, url: slot.url } : null;

export const buildFeaturedActionsPayload = (
  featuredActions: Record<FeaturedActionSlotKey, FeaturedAction>
): FeaturedActionsPayload => ({
  one: buildFeaturedActionSlotPayload(featuredActions.one),
  two: buildFeaturedActionSlotPayload(featuredActions.two),
});

const buildAppleEnhancedDesignPayload = (
  state: TemplateState['appleEnhancedDesign'],
  onLoad: TemplateState['_appleEnhancedDesignOnLoad'],
  mode: 'create' | 'update'
): AppleEnhancedDesignPayload | undefined => {
  const { type, images } = state;
  const featuredActions = state.featuredActions ?? createEmptyFeaturedActions();
  const onLoadFeaturedActions =
    onLoad.featuredActions ?? createEmptyFeaturedActions();
  const imageMatchesLoad = (key: AppleEnhancedDesignImageKey) =>
    (images[key] ?? '') === (onLoad.images[key] ?? '');

  const withFeaturedActions = (
    payload: Omit<AppleEnhancedDesignPayload, 'featuredActions'>
  ): AppleEnhancedDesignPayload =>
    payload.type === 'generic'
      ? {
          ...payload,
          featuredActions: buildFeaturedActionsPayload(featuredActions),
        }
      : payload;

  if (mode === 'create') {
    if (type === null || !images.artwork) return undefined;
    const populated = APPLE_ENHANCED_DESIGN_IMAGE_KEYS.filter((k) => images[k]);
    return withFeaturedActions({
      type,
      images: Object.fromEntries(populated.map((k) => [k, images[k]])),
    });
  }

  if (
    type === onLoad.type &&
    APPLE_ENHANCED_DESIGN_IMAGE_KEYS.every(imageMatchesLoad) &&
    (type !== 'generic' ||
      featuredActionsEqual(featuredActions, onLoadFeaturedActions))
  ) {
    return undefined;
  }

  if (type === null) {
    return { type: null };
  }

  if (!images.artwork) return undefined;

  const out: Record<string, string | null> = {};
  APPLE_ENHANCED_DESIGN_IMAGE_KEYS.forEach((k) => {
    if (k !== 'artwork' && imageMatchesLoad(k)) return;
    out[k] = images[k] === '' ? null : (images[k] ?? null);
  });
  return withFeaturedActions({ type, images: out });
};

export const stripAppleEnhancedDesignIfDisabled = <
  T extends { appleEnhancedDesign?: unknown },
>(
  payload: T,
  appleEnhancedDesignEnabled: boolean | null | undefined
): T => {
  if (!appleEnhancedDesignEnabled) {
    delete payload.appleEnhancedDesign;
  }
  return payload;
};
